import type { IconName } from '@/components/icons';

export type CustomerScreenKey =
  | 'home'
  | 'bills'
  | 'products'
  | 'wallet'
  | 'orders'
  | 'more'
  | 'support'
  | 'kyc'
  | 'notifications'
  | 'partnerApi'
  | 'sessions'
  | 'account'
  | 'quickpay'
  | 'customers'
  | 'distributor'
  | 'debts'
  | 'reports'
  | 'login'
  | 'register';

export type AdminScreenKey =
  | 'dashboard'
  | 'units'
  | 'games'
  | 'bills'
  | 'deposits'
  | 'quickpay'
  | 'recharge'
  | 'products'
  | 'categories'
  | 'ordering'
  | 'slides'
  | 'import'
  | 'sources'
  | 'payments'
  | 'users'
  | 'memberships'
  | 'financial'
  | 'ussd'
  | 'sham'
  | 'executor'
  | 'sms'
  | 'security'
  | 'site'
  | 'password'
  | 'support'
  | 'notifications'
  | 'account'
  | 'controls'
  | 'compliance'
  | 'staff'
  | 'login';

export type NavItem<K extends string = string> = {
  key: K;
  label: string;
  icon: IconName;
};

export type NavGroup<K extends string = string> = {
  group: string;
  items: NavItem<K>[];
};

/** Bottom nav — short labels like production */
export const CUSTOMER_BOTTOM_NAV: NavItem<CustomerScreenKey>[] = [
  { key: 'home', label: 'وحدات', icon: 'home' },
  { key: 'bills', label: 'فواتير', icon: 'bill' },
  { key: 'products', label: 'ألعاب', icon: 'game' },
  { key: 'wallet', label: 'المحفظة', icon: 'wallet' },
  { key: 'orders', label: 'الطلبات', icon: 'orders' },
];

export function customerSideNavGroups(opts: {
  role: string;
  apiEnabled?: boolean;
  quickPayLabel?: string;
}): NavGroup<CustomerScreenKey>[] {
  const groups: NavGroup<CustomerScreenKey>[] = [
    {
      group: 'الخدمات',
      items: [
        { key: 'home', label: 'اشحنلي وحدات', icon: 'home' },
        { key: 'bills', label: 'فواتير', icon: 'bill' },
        { key: 'products', label: 'اشحنلي ألعاب', icon: 'game' },
        { key: 'customers', label: 'زبائني', icon: 'users' },
        { key: 'wallet', label: 'المحفظة', icon: 'wallet' },
        {
          key: 'quickpay',
          label: opts.quickPayLabel ?? (opts.role === 'distributor' ? 'إرسال رصيد سريع' : 'الدفع السريع QR'),
          icon: 'zap',
        },
        { key: 'orders', label: 'طلباتي', icon: 'orders' },
      ],
    },
    {
      group: 'الحساب',
      items: [
        { key: 'support', label: 'الدعم الفني', icon: 'support' },
        { key: 'kyc', label: 'الوثائق', icon: 'shield' },
        { key: 'notifications', label: 'الإشعارات', icon: 'bell' },
        ...(opts.apiEnabled
          ? ([{ key: 'partnerApi', label: 'API', icon: 'settings' }] as NavItem<CustomerScreenKey>[])
          : []),
        { key: 'sessions', label: 'الجلسات النشطة', icon: 'device' },
        { key: 'account', label: 'حسابي', icon: 'user' },
      ],
    },
  ];

  if (opts.role === 'distributor') {
    groups.push({
      group: 'الموزع',
      items: [
        { key: 'distributor', label: 'شحن زبون', icon: 'deposit' },
        { key: 'debts', label: 'الديون', icon: 'wallet' },
        { key: 'reports', label: 'تقارير الموزع', icon: 'chart' },
      ],
    });
  }

  return groups;
}

/** Admin drawer — production groups/labels */
export const ADMIN_NAV_GROUPS: NavGroup<AdminScreenKey>[] = [
  {
    group: 'لوحة التحكم',
    items: [{ key: 'dashboard', label: 'لوحة القيادة', icon: 'home' }],
  },
  {
    group: 'الطلبات',
    items: [
      { key: 'units', label: 'طلبات الوحدات', icon: 'deposit' },
      { key: 'games', label: 'طلبات الألعاب', icon: 'game' },
      { key: 'bills', label: 'الفواتير اليدوية', icon: 'bill' },
      { key: 'deposits', label: 'طلبات شحن المحافظ', icon: 'wallet' },
      { key: 'quickpay', label: 'الدفع السريع', icon: 'zap' },
    ],
  },
  {
    group: 'الأقسام والمنتجات',
    items: [
      { key: 'recharge', label: 'فئات الشحن', icon: 'package' },
      { key: 'products', label: 'إدارة المنتجات', icon: 'game' },
      { key: 'categories', label: 'إدارة الأقسام', icon: 'orders' },
      { key: 'ordering', label: 'ترتيب العرض', icon: 'chart' },
      { key: 'slides', label: 'سلايدر الواجهة', icon: 'package' },
      { key: 'import', label: 'استيراد منتجات API', icon: 'zap' },
      { key: 'sources', label: 'مصادر المنتجات', icon: 'settings' },
    ],
  },
  {
    group: 'المالية والمستخدمون',
    items: [
      { key: 'payments', label: 'طرق الدفع', icon: 'wallet' },
      { key: 'users', label: 'المستخدمون والمحافظ', icon: 'users' },
      { key: 'memberships', label: 'العضويات', icon: 'shield' },
      { key: 'financial', label: 'التقارير المالية', icon: 'chart' },
    ],
  },
  {
    group: 'USSD والمزودون',
    items: [
      { key: 'sham', label: 'شام رصيد', icon: 'zap' },
      { key: 'ussd', label: 'إعدادات API Syria', icon: 'settings' },
      { key: 'executor', label: 'منفذ USSD', icon: 'device' },
      { key: 'sms', label: 'آخر رسائل الشركات', icon: 'bell' },
      { key: 'security', label: 'الأمان والسجل', icon: 'shield' },
    ],
  },
  {
    group: 'الخدمة والحساب',
    items: [
      { key: 'site', label: 'إعدادات الموقع', icon: 'settings' },
      { key: 'password', label: 'تغيير كلمة المرور', icon: 'settings' },
      { key: 'support', label: 'الدعم الفني', icon: 'support' },
      { key: 'notifications', label: 'الإشعارات', icon: 'bell' },
      { key: 'account', label: 'حسابي', icon: 'user' },
      { key: 'staff', label: 'الموظفون', icon: 'users' },
      { key: 'compliance', label: 'الامتثال', icon: 'shield' },
    ],
  },
];

export const ADMIN_NAV = ADMIN_NAV_GROUPS.flatMap((g) => g.items);

export function labelForCustomer(key: CustomerScreenKey): string {
  const all = [
    ...CUSTOMER_BOTTOM_NAV,
    ...customerSideNavGroups({ role: 'user' }).flatMap((g) => g.items),
  ];
  return all.find((i) => i.key === key)?.label ?? key;
}

export function labelForAdmin(key: AdminScreenKey): string {
  return ADMIN_NAV.find((i) => i.key === key)?.label ?? key;
}

/** Production wallet strip rule */
export function customerWalletMode(
  role: string,
  screen: CustomerScreenKey,
): 'units' | 'games' | 'none' {
  if (role === 'distributor' && (screen === 'home' || screen === 'distributor')) return 'none';
  if (screen === 'products') return 'games';
  if (screen === 'home' || screen === 'bills') return 'units';
  return 'none';
}
